import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Button from "web/react/components/LEGACY_button/button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { Button as EmoButton } from "web/react/emo/button";
import { useDomViewport } from "web/react/hooks/use-dom-viewport/use-dom-viewport";
import { openCustomerCaptureOverlay } from "web/redux/ducks/customer-capture-overlay";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import requester from "web/script/modules/requester";
import userProfiler from "web/script/modules/userprofiler";
import styles from "./save-search-button.module.css";

interface SaveSearchButtonProps {
    term: string;
    gender?: string | null;
    next: string;
    variant?: "emo" | "default";
}

export function SaveSearchButton({
    term = "",
    gender,
    next,
    variant = "default",
}: SaveSearchButtonProps): JSX.Element {
    const [isSaved, setIsSaved] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const dispatch = useDispatch();
    const { isMobileViewport, isTabletViewport } = useDomViewport();

    useEffect(() => {
        if (!userProfiler.isLoggedIn()) {
            return;
        }

        const query = new URLSearchParams({
            search_query: term,
            ...(gender ? { search_gender: gender } : {}),
        });

        requester(`/services/saved-search/?${query.toString()}`)
            .then(requester.toJSON)
            .then((data) => {
                setIsDisabled(false);
                setIsSaved(data.is_saved);
            })
            .catch((error) => {
                if (error.response.status !== 404) {
                    throw error;
                }
            })
            .finally(() => {
                setIsDisabled(false);
            });
    }, [term, gender]);

    function onClick(event: React.MouseEvent<HTMLButtonElement>): void {
        event.preventDefault();

        analytics.event("saved search", "clicked", term);

        if (!userProfiler.isLoggedIn()) {
            dispatch(
                openCustomerCaptureOverlay({
                    captureType: "signup_search",
                    searchTerm: term,
                    next,
                    ...(gender ? { searchGender: gender } : {}),
                })
            );
            return;
        }

        requester("/services/saved-search/", {
            method: "PUT",
            body: {
                search_query: term,
                search_gender: gender,
            } as any,
        })
            .then(requester.toJSON)
            .then((data) => {
                analytics.event("search", data.is_saved ? "saved" : "unsaved", term, true);
                setIsSaved(data.is_saved);
                setIsDisabled(true);
            })
            .catch((error) => {
                analytics.event("saved search error", "encountered", error, true);
                throw error;
            })
            .finally(() => {
                setIsDisabled(false);
            });
    }

    if (variant === "emo") {
        return (
            <EmoButton
                title={
                    isSaved
                        ? gettext("follow_button.label.following") // Following
                        : gettext("saved_search_button.initial_text") // Follow search
                }
                onClick={onClick}
                size={"sm"}
                variant={"secondary"}
                disabled={isDisabled}
                width={"fit-content"}
            />
        );
    }

    return (
        <Button
            className={styles.button}
            styleType={isSaved ? "primary" : "cta"}
            disabled={isDisabled}
            onClick={onClick}
            fullWidth={isMobileViewport || isTabletViewport}
        >
            {isSaved ? (
                <>
                    {/* Followed */}
                    <SVGIcon name="tick" />
                    {gettext("saved_search_button.saved_text")}
                </>
            ) : (
                <>
                    <SVGIcon name="plus" />
                    {/* Follow search */}
                    {gettext("saved_search_button.initial_text")}
                </>
            )}
        </Button>
    );
}
